/**
 * Applications Page - List all applications
 */
import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

function ApplicationsPage() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      const data = await api.getApplications();
      setApplications(data);
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      docs_pending: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-green-100 text-green-800',
      under_review: 'bg-purple-100 text-purple-800',
      approved: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredApplications = applications.filter(app =>
    app.business_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight">Applications</h1>
        <p className="text-muted-foreground mt-1">Merchant onboarding applications</p>
      </div>

      <Card>
        <CardHeader>
          <Input
            placeholder="Search applications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </CardHeader>
        <CardContent>
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <svg className="h-12 w-12 mx-auto text-muted-foreground mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-muted-foreground">No applications found</p>
              <Button onClick={() => navigate('/leads')} variant="outline" className="mt-4">
                Go to Leads
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-sm">Business</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Processor</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Boarding</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Template</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Created</th>
                    <th className="text-right py-3 px-4 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map((app) => (
                    <tr
                      key={app.id}
                      className="border-b hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/applications/${app.id}`)}
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium">{app.business_name || 'Unnamed'}</div>
                        <div className="text-sm text-muted-foreground">{app.legal_business_name || '-'}</div>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {(app.selected_processor || app.processor_slug)?.toUpperCase() || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm" data-testid={`boarding-status-${app.id}`}>
                        {app.boarding_status === 'submitted' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800" title={`External ID: ${app.external_application_id || 'pending'}`}>
                            Boarded to {app.processor_slug?.toUpperCase() || 'processor'}{app.external_application_id ? ` #${app.external_application_id}` : ''}
                          </span>
                        ) : app.boarding_status === 'error' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={app.boarding_error || 'Boarding submission failed'}>
                            Boarding Failed
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm capitalize">
                        {app.selected_template?.replace('_', ' ') || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                          {app.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {new Date(app.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/applications/${app.id}`);
                          }}
                        >
                          {app.status === 'submitted' ? 'View' : 'Continue'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ApplicationsPage;
